import React, { useRef, useContext } from 'react';
import styled from 'styled-components/macro';
import { useDay } from '@datepicker-react/hooks';
import isSameDay from 'date-fns/isSameDay';
import { DatePickerContext } from '../contexts/DatePickerContext';

interface IDayProps {
  dayLabel: string;
  date: Date;
  currentDay: Date | null;
  onDaySelected?: () => void;
}

const Day = ({ dayLabel, date, currentDay, onDaySelected }: IDayProps): JSX.Element => {
  const dayRef = useRef(null);
  const {
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    isStartDate,
    isEndDate,
    onDateSelect,
    onDateFocus,
    onDateHover,
  } = useContext(DatePickerContext);

  const { isSelected, isSelectedStartOrEnd, disabledDate, onClick, onKeyDown, onMouseEnter, tabIndex } = useDay({
    date,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateSelect,
    onDateHover,
    dayRef,
  });

  if (!dayLabel) {
    return <div />;
  }

  const onSelectDay = (): void => {
    onClick();
    if (onDaySelected) onDaySelected();
  };

  return (
    <DayButtonContainer
      isSelectedStartOrEnd={isSelectedStartOrEnd}
      isStartDate={isStartDate(date)}
      isEndDate={isEndDate(date)}
      isSelected={isSelected}
      isDisabled={disabledDate}
    >
      <DayButton
        onClick={onSelectDay}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        tabIndex={tabIndex}
        type="button"
        ref={dayRef}
        isCurrentDay={isSameDay(currentDay || new Date(), date)}
        isToday={isSameDay(date, new Date())}
        isDisabled={disabledDate}
        data-mixpanel-name="Calendar date"
        data-testid="day-button"
      >
        {dayLabel}
      </DayButton>
    </DayButtonContainer>
  );
};

export default Day;

interface IDayButtonContainerProps {
  isSelectedStartOrEnd: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isStartDate: boolean;
  isEndDate: boolean;
}

const DayButtonContainer = styled.span<IDayButtonContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ isSelectedStartOrEnd, isSelected, isDisabled }: IDayButtonContainerProps): string => {
    if (isSelectedStartOrEnd) {
      return 'hsl(189 70% 95%)';
    } else if (isSelected) {
      return 'hsl(189 70% 95%)';
    } else if (isDisabled) {
      return 'purple';
    } else {
      return 'transparent';
    }
  }};
  border-top-left-radius: ${({ isStartDate }: IDayButtonContainerProps): string => {
    return isStartDate ? '24px' : 'none';
  }};
  border-bottom-left-radius: ${({ isStartDate }: IDayButtonContainerProps): string => {
    return isStartDate ? '24px' : 'none';
  }};
  border-top-right-radius: ${({ isEndDate }: IDayButtonContainerProps): string => {
    return isEndDate ? '24px' : 'none';
  }};
  border-bottom-right-radius: ${({ isEndDate }: IDayButtonContainerProps): string => {
    return isEndDate ? '24px' : 'none';
  }};
  margin-bottom: 10px;
  margin-left: ${({ isStartDate }: IDayButtonContainerProps): string => {
    return isStartDate ? '12px' : '0';
  }};
  padding-right: ${({ isStartDate }: IDayButtonContainerProps): string => {
    return isStartDate ? '12px' : '0';
  }};
  margin-right: ${({ isEndDate }: IDayButtonContainerProps): string => {
    return isEndDate ? '12px' : '0';
  }};
  padding-left: ${({ isEndDate }: IDayButtonContainerProps): string => {
    return isEndDate ? '12px' : '0';
  }};
  text-align: center;
  ${({ isDisabled }: IDayButtonContainerProps): string => {
    return isDisabled ? 'cursor: not-allowed;' : '';
  }}
`;

interface IDayButtonProps {
  isCurrentDay: boolean;
  isToday: boolean;
  isDisabled: boolean;
}

const DayButton = styled.button<IDayButtonProps>`
  border-radius: 50%;
  border: ${({ isToday }: IDayButtonProps): string => {
    return isToday ? '1px solid hsl(190 80% 33%)' : 'transparent';
  }};
  background-color: ${({ isCurrentDay, isToday }: IDayButtonProps): string => {
    if (isCurrentDay) {
      return 'hsl(190 80% 33%)';
    } else if (isToday) {
      return '#fff';
    } else {
      return 'transparent';
    }
  }};
  cursor: ${({ isDisabled }: IDayButtonProps): string => {
    return isDisabled ? 'not-allowed;' : 'pointer';
  }};
  color: ${({ isCurrentDay }: IDayButtonProps): string => {
    return isCurrentDay ? 'hsl(0, 0%, 100%)' : 'hsl(233, 4%, 42%)';
  }};
  font-size: 12px;
  padding: 8px;

  &:hover {
    background-color: ${({ isCurrentDay }: IDayButtonProps): string => {
      if (isCurrentDay) {
        return 'hsl(190 80% 23%)';
      } else {
        return 'hsl(190 60% 83%)';
      }
    }};
  }

  &:focus {
    outline: none;
  }
`;
